import { useTranslation } from 'react-i18next';
import makeFirstLetterCapital from '../../../lib/helper-functions/makeFirstLetterCapital';
import mcpImg from '../../../assets/mcp.png';
import { useEffect, useState } from 'react';
import get_all_available_mcp_api_call from '../../../lib/api/mcp/get-all-available-mcp';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import get_all_connected_mcp_api_call from '../../../lib/api/mcp/get-all-connected-mcp';
import Popup from '../../../ui/popup/Popup';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import convertSnakeCaseToTitleCase from '../../../lib/helper-functions/convertSnakeCaseToTitleCase';
import connect_mcp_api_call from '../../../lib/api/mcp/connect-mcp';
import NoData from '../../../ui/noData/NoData';
import update_mcp_api_call from '../../../lib/api/mcp/update-mcp';
import delete_mcp_api_call from '../../../lib/api/mcp/delete-mcp';
import { submitLogToBugsnag } from '../../../lib/api/log';
import isNotEmptyString from '../../../lib/helper-functions/isNotEmptyString';
import removeHttpPrefix from '../../../lib/helper-functions/removeHttpPrefix';
import store from '../../../store/store';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  crossSVG,
  saveSVG,
  settingSVG,
  warningSVG,
  reloadArrowsSVG,
} from '../../../lib/allCustomSVGs';
import {
  APP_ENV,
  INTERCOM_ENABLED_ENVS,
} from '../../../lib/constants/central-api';
import {
  setIsPaymentPopupOpen,
  setPaymentPopupTemporaryMessage,
} from '../../../store/reducers/uiSlice';
import { useNavigate } from 'react-router-dom';
import save_mcp_oauth_api_call from '../../../lib/api/mcp/save-mcp-oauth';
import AISelectInput from '../../../ui/inputs/AISelectInput';
import MCPStatusCard from './MCPStatusCard';
import get_ai_tracker_basic_configs_api_call from '../../../lib/api/integration/get_ai_tracker_basic_configs_api_call';
import post_ai_tracker_basic_configs_api_call from '../../../lib/api/integration/post_ai_tracker_basic_configs_api_call';
import why_are_you_disconnecting_api_call from '../../../lib/api/mcp/why-are-you-disconnecting-api-call';
import DisconnectionReasonPopup from '../../../components/disconnectionReasonPopup/DisconnectionReasonPopup';
import refresh_mcp_accounts_api_call from '../../../lib/api/mcp/refresh-mcp-accounts';
import '../../../styles/pages/MCP/mcpConfigBox.scss';
import SearchSelectInput from '../../../ui/inputs/SearchSelectInput';

const MCP = ({
  searchValue,
  selectedFilter,
}: {
  searchValue: string;
  selectedFilter: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mcpList, setMcpList] = useState<any[]>([]);
  const [filteredMcpList, setFilteredMcpList] = useState<any[]>([]);

  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editingMCP, setEditingMCP] = useState<any>();
  const [preservedEditingMCP, setPreservedEditingMCP] = useState<any>();

  const [
    isActivationToggleConfirmationVisible,
    setIsActivationToggleConfirmationVisible,
  ] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDisconnectionReasonPopupOpen, setIsDisconnectionReasonPopupOpen] =
    useState(false);
  const [isSubmittingReason, setIsSubmittingReason] = useState(false);
  const [isRefreshingAccounts, setIsRefreshingAccounts] = useState(false);

  const [basicSetupData, setBasicSetupData] = useState<
    | {
        webshop_url: string;
        cms_type: string;
        optimization_ratio: number;
      }
    | undefined
  >(undefined);

  const [manualAccountFields, setManualAccountFields] = useState<Set<string>>(
    new Set()
  );

  const isTrackingScriptConnectionSuccess =
    store.getState().user?.isTrackingScriptConnectionSuccess ?? undefined;

  const toggleEditPopupModal = (isEditPopupOpen: boolean) => {
    if (isEditPopupOpen === false) {
      setEditingMCP(undefined);
      setPreservedEditingMCP(undefined);
      setManualAccountFields(new Set());
    }
    setIsEditPopupOpen(isEditPopupOpen);
    setIsActivationToggleConfirmationVisible(false);
    setIsSubmitting(false);
    setIsDeleting(false);
  };
  useEffect(() => {
    //* Verify if the user is redirected from an OAuth consent screen
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && isNotEmptyString(code) && state && isNotEmptyString(state)) {
      //* Removing parameters from browser URL
      window.history.replaceState({}, '', window?.location?.pathname);
      oAuthSaveHandler(code, state);
    } else {
      //* Verify if the user is redirected from TARA Chat
      const errorId = urlParams.get('errorId');
      if (errorId) {
        //* Removing parameters from browser URL
        window.history.replaceState({}, '', window?.location?.pathname);
        getMCPs(false, Number(errorId));
      } else {
        getMCPs();
      }
    }
  }, []);

  useEffect(() => {
    if (mcpList.length > 0) {
      validateAdUpAITrackingMCP();
    }
  }, [isTrackingScriptConnectionSuccess]);

  useEffect(() => {
    // Filter MCPs based on search value first
    const filteredBySearch = searchMCPs(searchValue, mcpList);

    // Apply status filter
    let finalFiltered = filteredBySearch;
    if (selectedFilter !== 'All Status') {
      finalFiltered = filteredBySearch?.filter((mcp) => {
        switch (selectedFilter) {
          case 'Connected':
            return mcp.is_active === true;
          case 'Disconnected':
            return mcp.is_active === false;
          case 'Not Connected':
            return mcp.connection_id === undefined;
          case 'Failed':
            return mcp.is_valid === false;
          case 'Not Configured':
            return (
              mcp.is_active === true &&
              mcp.is_default === false &&
              (!mcp.is_valid ||
                (mcp.oauth_required && isNotEmptyString(mcp.oauth_url)))
            );
          default:
            return true;
        }
      });
    }

    setFilteredMcpList(finalFiltered);
  }, [searchValue, selectedFilter, mcpList]);

  const getMCPs = async (
    noLoader: boolean = false,
    errorMcpId: number | undefined = undefined
  ) => {
    function processData(allMCPs: [], connectedMCPs: []) {
      const connectionMap = new Map();
      connectedMCPs.forEach((item: any) => {
        connectionMap.set(item.tara_mcp_id, item);
      });

      let hasActiveNonDefault = false;
      const processedData = allMCPs.map((item: any) => {
        const mcpConnectionId = connectionMap.get(item.id)?.id;
        const isAdUpAITracking =
          connectionMap.get(item.id)?.tara_mcp?.slug === 'adup_ai_tracking';
        const isActive = connectionMap.get(item.id)?.is_active;
        const isDefault = item?.is_default;

        let formattedMCP = {
          id: item?.id,
          mcp_slug: item?.slug,
          mcp_name: item?.mcp_name,
          credentials_fields:
            item?.slug === 'google_ads' //? Temporary solution for Google Ads MCP account fetch issue in BE
              ? [...item?.credentials_fields, 'MANAGER_ACCOUNT_ID']
              : item?.credentials_fields,
          logo: item?.logo,
          connection_id: mcpConnectionId,
          credentials: connectionMap.get(item.id)?.credentials,
          is_active: isActive,
          is_valid: connectionMap.get(item.id)?.is_valid ?? true,
          is_default: isDefault,
          configs: item?.configs,
          oauth_url: item?.oauth_url,
          oauth_required: connectionMap.get(item.id)?.oauth_required,
          accounts: connectionMap.get(item.id)?.accounts,
          isAdUpAITracking,
        };

        if (isDefault === false && isActive === true) {
          hasActiveNonDefault = true;
        }

        return formattedMCP;
      });

      //* Trigger intercom event when the user connects at least 1 third party MCP
      if (INTERCOM_ENABLED_ENVS.includes(APP_ENV) && hasActiveNonDefault) {
        console.log('INTERCOM Event Triggered: mcp-connected');
        // @ts-ignore:next-line
        window.Intercom && window.Intercom('trackEvent', 'mcp-connected');
      }

      return processedData;
    }

    try {
      setIsLoading(!noLoader);
      const allMCPs = get_all_available_mcp_api_call();
      allMCPs.then((allMCPs: []) => {
        const connectedMCPs = get_all_connected_mcp_api_call();
        connectedMCPs.then((connectedMCPs: []) => {
          const data = processData(allMCPs, connectedMCPs);
          setMcpList(data);
          setIsLoading(false);
          if (errorMcpId) {
            const mcp = data.find(
              (mcp: any) => mcp?.connection_id === errorMcpId
            );
            if (mcp) {
              if (mcp?.isAdUpAITracking) {
                navigate(
                  `/tracking/integration?connection_id=${mcp?.connection_id}&is_active=${mcp?.is_active}`
                );
              } else {
                oAuthRedirectHandler(mcp);
              }
            }
          }
        });
      });
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching data:', error);
      submitLogToBugsnag('error', `Error in fetching MCP data: ${error}`);
    }
  };

  const validateAdUpAITrackingMCP = async () => {
    const adUpAITrackingMCP: any = mcpList.find(
      (item: any) => item?.isAdUpAITracking
    );
    const isActive = adUpAITrackingMCP?.is_active;
    const mcpConnectionId = adUpAITrackingMCP?.connection_id;

    if (
      (isTrackingScriptConnectionSuccess === 'true' && !isActive) ||
      (isTrackingScriptConnectionSuccess === 'false' && isActive)
    ) {
      //* Update MCP if tracking script connection status is not the same as the MCP's is_active
      await update_mcp_api_call(
        mcpConnectionId,
        {},
        isTrackingScriptConnectionSuccess === 'true' ? true : false,
        undefined,
        false
      );

      const updatedList = mcpList.map((mcp: any) => {
        if (mcp.isAdUpAITracking) {
          return {
            ...mcp,
            is_active:
              isTrackingScriptConnectionSuccess === 'true' ? true : false,
          };
        }
        return mcp;
      });
      setMcpList(updatedList);
    }
  };

  const constructDynamicOauthURL = (
    providerOauthURL: string,
    payload: object
  ) => {
    // @ts-ignore:next-line
    const STATE = btoa(JSON.stringify(payload));
    const REDIRECT_URI = `${window.location.origin}/integrations`;

    const URL = `${providerOauthURL}&redirect_uri=${REDIRECT_URI}&state=${STATE}`;
    return URL;
  };

  const oAuthRedirectHandler = (
    editingMCP: any,
    isToggleClick: boolean = false,
    toggleStatus: boolean = false
  ) => {
    const mode = editingMCP?.is_active !== undefined ? 'update' : 'add';

    const isOauth =
      editingMCP?.oauth_url &&
      isNotEmptyString(editingMCP?.oauth_url) &&
      (mode === 'update' ? editingMCP?.oauth_required : true);

    if (isOauth) {
      try {
        setIsSubmitting(true);
        const redirectURL = constructDynamicOauthURL(
          editingMCP?.oauth_url,
          editingMCP
        );
        window.location.href = redirectURL; //? Redirecting to respective OAuth consent screen
      } catch (error) {
        setIsSubmitting(false);
        console.error(
          `${editingMCP?.configs?.oauth?.provider} OAuth - Error connecting MCP:`,
          error
        );
        submitLogToBugsnag(
          'error',
          `${editingMCP?.configs?.oauth?.provider} OAuth - Error in connecting MCP: ${error}`
        );
        store.dispatch(
          setMessage({
            message: `${t('MCP-OauthFailed')}`,
            messageType: 'error',
          })
        );
      }
    } else {
      if (isToggleClick) {
        handleStatusToggle(editingMCP, toggleStatus);
      } else {
        setEditingMCP(editingMCP);
        toggleEditPopupModal(true);
      }
    }
  };

  const oAuthSaveHandler = async (code: string, state: string) => {
    try {
      // @ts-ignore:next-line
      const decodedState = JSON.parse(atob(state));
      const mcp = { ...decodedState, oauthCode: code };

      const response = await save_mcp_oauth_api_call(mcp.id, mcp.oauthCode);

      await getMCPs();

      if (response?.success) {
        if (mcp?.credentials_fields?.length > 0) {
          setEditingMCP({
            ...mcp,
            accounts: response?.data?.accounts,
          });
          toggleEditPopupModal(true);
        } else {
          onSubmitHandler(mcp);
        }
      } else {
        setEditingMCP(mcp);
        toggleEditPopupModal(true);
        store.dispatch(
          setMessage({
            message: isNotEmptyString(response?.message)
              ? response?.message
              : `${t('MCP-OauthFailed')}`,
            messageType: isNotEmptyString(response?.message)
              ? 'warning'
              : 'error',
          })
        );
      }
    } catch (error) {
      console.error(`OAuth - Failed to decode state:`, error);
    }
  };

  const onSubmitHandler = async (editingMCP: any) => {
    const mode = editingMCP?.is_active !== undefined ? 'update' : 'add';
    if (mode === 'add') {
      try {
        setIsSubmitting(true);
        const response = await connect_mcp_api_call(
          editingMCP.id,
          editingMCP.credentials
        );
        if (response?.success) {
          setIsSubmitting(false);
          if (response?.data?.is_valid) {
            getMCPs(false);
            toggleEditPopupModal(false);
            if (editingMCP?.isAIOptimizeRedirection) {
              navigate('/ai-optimizations/automated?integrationsRedirect=true');
            }
          } else {
            getMCPs(true);
            setEditingMCP({
              ...editingMCP,
              connection_id: response?.data?.id,
              credentials: response?.data?.credentials,
              is_active: true,
              is_valid: false,
            });
            store.dispatch(
              setMessage({
                message: `${t('MCP-InvalidCredentials')}`,
                messageType: 'warning',
              })
            );
          }
        } else if (response?.data && response?.data?.mcp_limit) {
          toggleEditPopupModal(false);
          store.dispatch(
            setPaymentPopupTemporaryMessage(
              `⚠️ ${t('MCP-LimitReached', {
                mcpLimit: response?.data?.mcp_limit,
              })}`
            )
          );
          store.dispatch(setIsPaymentPopupOpen(true));
        } else {
          setIsSubmitting(false);
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error('Error connecting MCP:', error);
        submitLogToBugsnag('error', `Error in connecting MCP: ${error}`);
      }
    } else {
      try {
        setIsSubmitting(true);
        const response = await update_mcp_api_call(
          editingMCP.connection_id,
          editingMCP.credentials,
          editingMCP.is_active
        );
        if (response?.success) {
          setIsSubmitting(false);
          if (
            response?.data?.is_valid ||
            !editingMCP?.is_active ||
            editingMCP?.is_default
          ) {
            getMCPs(false);
            toggleEditPopupModal(false);
            if (editingMCP?.isAIOptimizeRedirection) {
              navigate('/ai-optimizations/automated?integrationsRedirect=true');
            }
          } else {
            getMCPs(true);
            setEditingMCP({
              ...editingMCP,
              is_valid: false,
            });
            setIsActivationToggleConfirmationVisible(false);
            store.dispatch(
              setMessage({
                message: `${t('MCP-InvalidCredentials')}`,
                messageType: 'warning',
              })
            );
          }
        } else if (response?.data && response?.data?.mcp_limit) {
          toggleEditPopupModal(false);
          store.dispatch(
            setPaymentPopupTemporaryMessage(
              `⚠️ ${t('MCP-LimitReached', {
                mcpLimit: response?.data?.mcp_limit,
              })}`
            )
          );
          store.dispatch(setIsPaymentPopupOpen(true));
        } else {
          setIsSubmitting(false);
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error('Error updating MCP:', error);
        submitLogToBugsnag('error', `Error in updating MCP: ${error}`);
      }
    }
  };

  const isLastActiveMCP = () => {
    const activeMCPs = mcpList.filter(
      (mcp) => mcp.is_active === true && !mcp.is_default
    );
    return activeMCPs.length === 1 && activeMCPs[0].id === editingMCP?.id;
  };

  const handleDisconnectionReasonSubmit = async (reason: string) => {
    try {
      setIsSubmittingReason(true);
      await why_are_you_disconnecting_api_call(reason);
      await performDelete();
      setIsDisconnectionReasonPopupOpen(false);
      setIsSubmittingReason(false);
    } catch (error) {
      setIsSubmittingReason(false);
      console.error('Error submitting disconnection reason:', error);
      submitLogToBugsnag(
        'error',
        `Error in submitting disconnection reason: ${error}`
      );
      store.dispatch(
        setMessage({
          message: `${t('SomethingWentWrong-trying-again-msg')}`,
          messageType: 'error',
        })
      );
    }
  };

  const performDelete = async () => {
    try {
      const mcpToDelete = preservedEditingMCP || editingMCP;
      const response = await delete_mcp_api_call(mcpToDelete.connection_id);
      if (response) {
        getMCPs();
        setEditingMCP(undefined);
        setPreservedEditingMCP(undefined);
        setManualAccountFields(new Set());
        setIsEditPopupOpen(false);
        setIsActivationToggleConfirmationVisible(false);
        setIsSubmitting(false);
        setIsDeleting(false);
      } else {
        throw new Error('Delete API failed');
      }
    } catch (error) {
      console.error('Error deleting MCP:', error);
      submitLogToBugsnag('error', `Error in deleting MCP: ${error}`);
      throw error;
    }
  };

  const performDeleteWithLoading = async () => {
    setIsDeleting(true);
    try {
      await performDelete();
    } catch (error) {
      setIsDeleting(false);
      throw error;
    }
  };

  const deleteHandler = async (e: any) => {
    e.preventDefault();

    if (isLastActiveMCP()) {
      setPreservedEditingMCP(editingMCP);
      setIsEditPopupOpen(false);
      setIsActivationToggleConfirmationVisible(false);
      setIsSubmitting(false);
      setIsDeleting(false);
      setIsDisconnectionReasonPopupOpen(true);
    } else {
      await performDeleteWithLoading();
    }
  };

  const handleGoBack = () => {
    setIsDisconnectionReasonPopupOpen(false);
    setEditingMCP(preservedEditingMCP);
    setIsEditPopupOpen(true);
  };

  const handleStatusToggle = async (mcp: any, newStatus: boolean) => {
    if (
      mcp?.isAdUpAITracking &&
      isTrackingScriptConnectionSuccess !== 'true' &&
      newStatus
    ) {
      store.dispatch(
        setMessage({
          message: `${t('IntegrationPending-2')}`,
          messageType: 'warning',
        })
      );
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await update_mcp_api_call(
        mcp.connection_id,
        mcp.credentials,
        newStatus,
        undefined,
        false
      );

      if (response?.success) {
        if (mcp?.isAdUpAITracking) {
          //? Update the tracking basic setup for "is_tracker_active" change
          const getResponse = basicSetupData
            ? basicSetupData
            : await get_ai_tracker_basic_configs_api_call();
          setBasicSetupData(getResponse);
          await post_ai_tracker_basic_configs_api_call(
            getResponse?.webshop_url,
            getResponse?.cms_type,
            getResponse?.optimization_ratio,
            newStatus
          );
        }
        //? Update the mcpList state directly
        setMcpList((prevList) =>
          prevList.map((item) => {
            if (item.id === mcp.id) {
              return {
                ...item,
                is_active: newStatus,
                is_valid: response?.data?.is_valid ?? item.is_valid,
              };
            }
            return item;
          })
        );
      } else if (response?.data && response?.data?.mcp_limit) {
        store.dispatch(
          setPaymentPopupTemporaryMessage(
            `⚠️ ${t('MCP-LimitReached', {
              mcpLimit: response?.data?.mcp_limit,
            })}`
          )
        );
        store.dispatch(setIsPaymentPopupOpen(true));
      }
    } catch (error) {
      console.error('Error updating MCP status:', error);
      submitLogToBugsnag('error', `Error in updating MCP status: ${error}`);
      store.dispatch(
        setMessage({
          message: `${t('SomethingWentWrong-trying-again-msg')}`,
          messageType: 'error',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const searchMCPs = (searchValue: string, mcpList: any[]) => {
    if (isNotEmptyString(searchValue)) {
      const searchLower = searchValue.toLowerCase();
      const startsWithMatches = mcpList.filter((mcp) =>
        mcp.mcp_name.toLowerCase().startsWith(searchLower)
      );
      const includesMatches = mcpList.filter(
        (mcp) =>
          mcp.mcp_name.toLowerCase().includes(searchLower) &&
          !mcp.mcp_name.toLowerCase().startsWith(searchLower)
      );
      return [...startsWithMatches, ...includesMatches];
    }
    return mcpList;
  };

  const handleRefreshAccounts = async (field: string) => {
    if (!editingMCP?.connection_id) return;

    try {
      setIsRefreshingAccounts(true);
      const response = await refresh_mcp_accounts_api_call(
        editingMCP.connection_id
      );

      if (response && response.accounts) {
        setEditingMCP({
          ...editingMCP,
          accounts: {
            ...editingMCP.accounts,
            [field]: response.accounts[field] || editingMCP.accounts[field],
          },
        });

        store.dispatch(
          setMessage({
            message: `${t('MCP-AccountsRefreshed')}`,
            messageType: 'success',
          })
        );
      } else {
        store.dispatch(
          setMessage({
            message: `${t('MCP-AccountsRefreshFailed')}`,
            messageType: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Error refreshing accounts:', error);
      store.dispatch(
        setMessage({
          message: `${t('MCP-AccountsRefreshFailed')}`,
          messageType: 'error',
        })
      );
    } finally {
      setIsRefreshingAccounts(false);
    }
  };

  return (
    <>
      <div className='taraSearch'>
        <div>
          <div className='integrationContainer'>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {filteredMcpList?.length > 0 ? (
                  filteredMcpList.map((mcp) => (
                    <MCPStatusCard
                      key={mcp.id}
                      hasOAuth={isNotEmptyString(mcp?.oauth_url)}
                      info={makeFirstLetterCapital(mcp.mcp_name)}
                      active={mcp.is_active?.toString() ?? 'null'}
                      image={isNotEmptyString(mcp?.logo) ? mcp?.logo : mcpImg}
                      onClick={() => {
                        if (mcp?.isAdUpAITracking) {
                          navigate(
                            `/tracking/integration?connection_id=${mcp?.connection_id}&is_active=${mcp?.is_active}`
                          );
                        } else {
                          oAuthRedirectHandler(mcp);
                        }
                      }}
                      disabled={isSubmitting || isDeleting || isLoading}
                      hideConfigure={mcp?.is_default && !mcp?.isAdUpAITracking}
                      iconTitle={
                        !mcp?.is_default && !mcp?.is_valid
                          ? t('MCP-InvalidCredentials')
                          : !mcp?.is_default &&
                            mcp?.oauth_required &&
                            isNotEmptyString(mcp?.oauth_url)
                          ? t('MCP-OauthConsentRequired')
                          : ''
                      }
                      icon={
                        mcp?.is_active &&
                        !mcp?.is_default &&
                        (!mcp?.is_valid ||
                          (mcp?.oauth_required &&
                            isNotEmptyString(mcp?.oauth_url)))
                          ? warningSVG
                          : undefined
                      }
                      onStatusToggle={(newStatus) =>
                        oAuthRedirectHandler(mcp, true, newStatus)
                      }
                    />
                  ))
                ) : (
                  <div
                    style={{
                      width: '100%',
                    }}
                  >
                    <NoData
                      title={
                        isNotEmptyString(searchValue)
                          ? t('NoMCP-Search', {
                              mcpName: searchValue,
                            })
                          : t('NoMCP')
                      }
                      subTitle={
                        !isNotEmptyString(searchValue)
                          ? t('ContactSupport')
                          : undefined
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={() => toggleEditPopupModal(false)}
        trigger={isEditPopupOpen}
      >
        <form
          id='mcpModal-form-update'
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitHandler(editingMCP);
          }}
          className='mcpConfigBox'
        >
          <div className='mcpConfigBox__title'>
            <div className='mcpConfigBox__title--main'>
              <div className='mcpConfigBox__title--main-image'>
                <img
                  src={editingMCP?.logo}
                  alt={makeFirstLetterCapital(editingMCP?.mcp_name)}
                />
              </div>
              <h1 className='mcpConfigBox__title--main-text'>
                {t('ManageMCP', {
                  mcpName: makeFirstLetterCapital(editingMCP?.mcp_name),
                })}
              </h1>
            </div>

            <div className='mcpConfigBox__title--tags'>
              {!editingMCP?.is_default && !editingMCP?.is_valid && (
                <div
                  className='collapseList__item--top-left-tag collapseList__item--top-left-tag-yellow'
                  style={{ width: 'fit-content', marginTop: '0.5rem' }}
                >
                  ⚠️ {t('MCP-InvalidCredentials')}
                </div>
              )}

              {!editingMCP?.is_default &&
                editingMCP?.oauth_required &&
                isNotEmptyString(editingMCP?.oauth_url) && (
                  <div
                    className='collapseList__item--top-left-tag collapseList__item--top-left-tag-yellow'
                    style={{ width: 'fit-content', marginTop: '0.5rem' }}
                  >
                    ⚠️ {t('MCP-OauthConsentRequired')}
                  </div>
                )}
            </div>
          </div>
          <div className='mcpConfigBox__content'>
            <div className='mcpConfigBox__content--inputs'>
              {editingMCP?.credentials_fields?.map(
                (field: string, index: number) => (
                  <div
                    className='mcpConfigBox__content--inputs-field'
                    key={index}
                  >
                    {editingMCP?.accounts &&
                    Array.isArray(editingMCP?.accounts?.[field]) &&
                    editingMCP?.accounts?.[field]?.length > 0 ? (
                      <>
                        {/* <AISelectInput
                          defaultValue={
                            editingMCP?.credentials?.[field] ||
                            editingMCP?.accounts?.[field]?.[0]?.OptionValue ||
                            ''
                          }
                          setSelect={(value: string) => {
                            if (value === 'manual_account') {
                              setManualAccountFields(
                                (prev) =>
                                  new Set(Array.from(prev).concat(field))
                              );
                            } else {
                              setManualAccountFields((prev) => {
                                const newSet = new Set(prev);
                                newSet.delete(field);
                                return newSet;
                              });
                            }
                            setEditingMCP({
                              ...editingMCP,
                              credentials: {
                                ...editingMCP?.credentials,
                                [field]:
                                  field === 'MYSHOPIFY_DOMAIN'
                                    ? removeHttpPrefix(value)
                                    : value,
                              },
                            });
                          }}
                          selectOptionArr={[
                            ...editingMCP?.accounts?.[field].map(
                              (value: any) => ({
                                OptionValue: value?.OptionValue,
                                OptionName: `${value?.OptionName} ${
                                  value?.OptionValue !== value?.OptionName
                                    ? `(${value?.OptionValue})`
                                    : ''
                                }`,
                              })
                            ),
                            {
                              OptionValue: 'manual_account',
                              OptionName: t('MCP-ManualAccount', {
                                credentialFieldName:
                                  convertSnakeCaseToTitleCase(field),
                              }),
                            },
                          ]}
                          placeholder={convertSnakeCaseToTitleCase(field)}
                          id={`${field}-input`}
                          refreshButton={{
                            onClick: () => handleRefreshAccounts(field),
                            isLoading: isRefreshingAccounts,
                            icon: reloadArrowsSVG,
                          }}
                        /> */}
                        <SearchSelectInput
                          defaultValue={
                            editingMCP?.credentials?.[field] ||
                            editingMCP?.accounts?.[field]?.[0]?.OptionValue ||
                            ''
                          }
                          setSelect={(value: string) => {
                            if (value === 'manual_account') {
                              setManualAccountFields(
                                (prev) =>
                                  new Set(Array.from(prev).concat(field))
                              );
                            } else {
                              setManualAccountFields((prev) => {
                                const s = new Set(prev);
                                s.delete(field);
                                return s;
                              });
                            }
                            setEditingMCP({
                              ...editingMCP,
                              credentials: {
                                ...editingMCP?.credentials,
                                [field]:
                                  field === 'MYSHOPIFY_DOMAIN'
                                    ? removeHttpPrefix(value)
                                    : value,
                              },
                            });
                          }}
                          selectOptionArr={[
                            ...editingMCP?.accounts?.[field].map(
                              (value: any) => ({
                                OptionValue: value?.OptionValue,
                                OptionName: `${value?.OptionName} ${
                                  value?.OptionValue !== value?.OptionName
                                    ? `(${value?.OptionValue})`
                                    : ''
                                }`,
                              })
                            ),
                            {
                              OptionValue: 'manual_account',
                              OptionName: t('MCP-ManualAccount', {
                                credentialFieldName:
                                  convertSnakeCaseToTitleCase(field),
                              }),
                            },
                          ]}
                          placeholder={convertSnakeCaseToTitleCase(field)}
                          id={`${field}-input`}
                          required={
                            !(
                              field === 'MANAGER_ACCOUNT_ID' &&
                              editingMCP?.mcp_slug === 'google_ads'
                            )
                          }
                          refreshButton={{
                            onClick: () => handleRefreshAccounts(field),
                            isLoading: isRefreshingAccounts,
                            icon: reloadArrowsSVG,
                          }}
                        />
                        {manualAccountFields.has(field) && (
                          <AINormalInput
                            key={`${field}-manual`}
                            changeListeners={[
                              (event) => {
                                setEditingMCP({
                                  ...editingMCP,
                                  credentials: {
                                    ...editingMCP?.credentials,
                                    [`${field}_manual`]:
                                      field === 'MYSHOPIFY_DOMAIN'
                                        ? removeHttpPrefix(event.target.value)
                                        : event.target.value,
                                  },
                                });
                              },
                            ]}
                            id={`${field}-manual-input`}
                            value={
                              editingMCP?.credentials?.[`${field}_manual`] || ''
                            }
                            required={true}
                            innerPlaceholder={
                              t(`EnterYour`) +
                              ' ' +
                              convertSnakeCaseToTitleCase(field) +
                              (field === 'MYSHOPIFY_DOMAIN'
                                ? ' ("example.myshopify.com")'
                                : '')
                            }
                            pattern={
                              (field === 'MANAGER_ACCOUNT_ID' ||
                                field === 'ACCOUNT_ID') &&
                              editingMCP?.mcp_slug === 'google_ads'
                                ? '^[0-9]+$'
                                : undefined
                            }
                            title={
                              (field === 'MANAGER_ACCOUNT_ID' ||
                                field === 'ACCOUNT_ID') &&
                              editingMCP?.mcp_slug === 'google_ads'
                                ? t('OnlyNumeric-validation')
                                : undefined
                            }
                          />
                        )}
                      </>
                    ) : field === 'MANAGER_ACCOUNT_ID' &&
                      editingMCP?.mcp_slug === 'google_ads' &&
                      editingMCP?.accounts &&
                      Array.isArray(editingMCP?.accounts?.['ACCOUNT_ID']) &&
                      editingMCP?.accounts?.['ACCOUNT_ID']?.length > 0 &&
                      editingMCP?.credentials?.['ACCOUNT_ID'] !==
                        'manual_account' ? null : (
                      <AINormalInput
                        key={index}
                        changeListeners={[
                          (event) => {
                            setEditingMCP({
                              ...editingMCP,
                              credentials: {
                                ...editingMCP?.credentials,
                                [field]:
                                  field === 'MYSHOPIFY_DOMAIN'
                                    ? removeHttpPrefix(event.target.value)
                                    : event.target.value,
                              },
                            });
                          },
                        ]}
                        id={`${field}-input`}
                        value={editingMCP?.credentials?.[field] || ''}
                        placeholder={
                          field === 'MANAGER_ACCOUNT_ID' &&
                          editingMCP?.mcp_slug === 'google_ads'
                            ? `${convertSnakeCaseToTitleCase(field)} (${t(
                                'Optional'
                              )})`
                            : convertSnakeCaseToTitleCase(field)
                        }
                        required={
                          !(
                            field === 'MANAGER_ACCOUNT_ID' &&
                            editingMCP?.mcp_slug === 'google_ads'
                          )
                        }
                        innerPlaceholder={
                          t(`EnterYour`) +
                          ' ' +
                          convertSnakeCaseToTitleCase(field) +
                          (field === 'MYSHOPIFY_DOMAIN'
                            ? ' ("example.myshopify.com")'
                            : '')
                        }
                        pattern={
                          (field === 'MANAGER_ACCOUNT_ID' ||
                            field === 'ACCOUNT_ID') &&
                          editingMCP?.mcp_slug === 'google_ads'
                            ? '^[0-9]+$'
                            : undefined
                        }
                        title={
                          (field === 'MANAGER_ACCOUNT_ID' ||
                            field === 'ACCOUNT_ID') &&
                          editingMCP?.mcp_slug === 'google_ads'
                            ? t('OnlyNumeric-validation')
                            : undefined
                        }
                      />
                    )}
                  </div>
                )
              )}
            </div>
            <div className='mcpConfigBox__content--info'>
              <p
                style={{
                  marginTop: '1rem',
                  fontSize: '1rem',
                  fontStyle: 'italic',
                  color: '#7a7a7a',
                }}
              >
                {isActivationToggleConfirmationVisible
                  ? ' * ' + t('MCP-ActivationToggleConfirmation')
                  : ''}
              </p>
            </div>
          </div>
          <div className='mcpConfigBox__buttons'>
            {editingMCP?.is_active !== undefined ? (
              <>
                {!editingMCP?.is_default && (
                  <MainSquareButton
                    value={
                      isDeleting ? `${t('Disconnecting')}...` : t('Disconnect')
                    }
                    disabled={isSubmitting || isDeleting}
                    type='button'
                    form='mcpModal-form-update'
                    onClick={deleteHandler}
                    classes='mcpConfigBox__buttons--danger'
                    icon={crossSVG}
                  />
                )}
                <MainSquareButton
                  value={
                    isSubmitting
                      ? `${t('Updating')}...`
                      : !editingMCP?.is_default &&
                        editingMCP?.oauth_required &&
                        isNotEmptyString(editingMCP?.oauth_url)
                      ? t('Authorize')
                      : t('Update')
                  }
                  disabled={isSubmitting || isDeleting}
                  type='submit'
                  form='mcpModal-form-update'
                  classes='mcpConfigBox__buttons--update'
                  icon={settingSVG}
                />
              </>
            ) : (
              <MainSquareButton
                value={isSubmitting ? `${t('Connecting')}...` : t('ConnectMCP')}
                disabled={isSubmitting || isDeleting}
                type='submit'
                classes='mcpConfigBox__buttons--update'
                form='mcpModal-form-update'
                icon={saveSVG}
              />
            )}
          </div>
        </form>
      </Popup>

      <DisconnectionReasonPopup
        isOpen={isDisconnectionReasonPopupOpen}
        onClose={() => {
          setIsDisconnectionReasonPopupOpen(false);
        }}
        onSubmit={handleDisconnectionReasonSubmit}
        onGoBack={handleGoBack}
        isSubmitting={isSubmittingReason}
      />
    </>
  );
};

export default MCP;
